import { useTransactionEnricher } from "./useTransactionEnricher";
import {
    ActionStatusHelper,
    DomainDetailHook,
    useAppEnum,
    DOMAIN_CONFIGS,
    DomainHookProps,
    ActionResponse,
    useDetailLifeCycle,
} from "@/Shared";

import { useTransaction } from "@/OneLiners";
import { TransactionViewModel } from "../ViewModels/TransactionViewModel";
import { TransactionModel } from "../TransactionModel";


/**
 * useTransactionDetail hook
 *
 * Domain-specific detail hook for Transaction entities.
 * Acts as the orchestration layer between:
 * - domain data access and enrichment,
 * - permissions and access rights,
 * - UI feedback mechanisms (toasts, confirmations),
 * - navigation and redirection concerns.
 *
 * Responsibilities:
 * - Initialize the TransactionViewModel when data is available.
 * - Enrich the transaction with domain-specific computed data.
 * - Handle delete or update operations with access control.
 * - Provide a read-only or editable VM depending on permissions.
 * - Expose entity VM, action executor, and access rights to the UI layer.
 *
 * @date: March 2, 2026
 * @author: Samano CASTRE
 * 
 * @see DomainHookProps
 * @see DomainDetailHook<TransactionViewModel>
 *
 * @param props DomainHookProps
 * @returns DomainDetailHook<TransactionViewModel>
 */
export function useTransactionDetail(props: DomainHookProps): DomainDetailHook<TransactionViewModel> {
    const { permissionHook, accessRight } = props;
    const { domainType } = DOMAIN_CONFIGS.Transaction;

    /**
     * Transaction-related enums exposed to the UI layer
     */
    const { TransactionEnums } = useAppEnum();

    /**
     * Transaction domain CRUD operations and state
     */
    const transactionHook = useTransaction();

    /**
     * Transaction domain enrichment facade
     */
    const {
        enricherResponse,
        getStrictEnrichedTransaction,
    } = useTransactionEnricher();

    /**
     * Combined response acting as a single readiness contract
     * for initializing the detail view
     */
    const response: ActionResponse = {
        status: ActionStatusHelper.combine(
            transactionHook.stateResponse.status,
            enricherResponse.status,
            permissionHook.stateResponse.status
        ),
        error: transactionHook.stateResponse.error ??
            enricherResponse.error
    };

    /**
     * Detail life cycle hook
     *
     * Handles:
     * - Entity resolution (queryId / entityId)
     * - ViewModel construction
     * - Post-fetch or post-update handling
     * - Provides public API to the UI layer
     */
    const lifeCycleHook = useDetailLifeCycle({
        response,
        domainType,
        props,
        entityHook: transactionHook,
        buildVM: (currentEntity: TransactionModel, isDisplaying: boolean) => {
            return new TransactionViewModel({
                transaction: getStrictEnrichedTransaction(currentEntity),
                viewEnums: TransactionEnums,
                accessRight,
                isDisplaying
            })
        },
    });

    /**
     * Public API exposed to the UI layer
     */
    return {
        ...lifeCycleHook
    }
}